import { Component, computed, inject, input, viewChild } from '@angular/core';
import { PersonTypeEnum } from '../../../../../../../../types/src/common/enum/person-type.enum';
import { CenterObject } from '@it2go/types';
import { OrganizationStatutaryRepresentativeTableStore } from './organization-statutary-representative-table.store';
import {
    UpsertContactPersonDialogComponent,
} from '../upsert-contact-person-dialog/upsert-contact-person-dialog.component';
import { fb, isAllowed, routeParams } from '../../../../shared/shared.global-service';
import { getPersonsFrag } from './organization-statutary-representative-table.query';
import { CardTableComponent } from '../../../../shared/components/kit/table/card-table.component';
import { contentGlobalSuccess } from '../../../content.error';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { FilterInput } from '@it2go/data-grid';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    OrganizationContactSelectDialogComponent,
} from '../organization-contact-select-dialog/organization-contact-select-dialog.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import {
    OrganizationCenterPersonObject,
} from '@libs/types/src/organization/object-type/organization-center-person.object';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { FormatPersonPipe } from '../../../../shared/pipes/format-person.pipe';
import { ShortDatePipe } from '../../../../shared/pipes/short-date.pipe';

@Component({
    selector: 'app-organization-statutary-representative-table',
    templateUrl: 'organization-statutary-representative-table.component.html',
    providers: [OrganizationStatutaryRepresentativeTableStore],
    imports: [
        ButtonComponent,
        Tooltip,
        TranslatePipe,
        CardTableComponent,
        PrimeTemplate,
        FormatPersonPipe,
        ShortDatePipe,
    ],
})
export class OrganizationStatutaryRepresentativeTableComponent {

    public readonly store = inject(OrganizationStatutaryRepresentativeTableStore);
    public readonly confirm = inject(ConfirmBuilder);
    public readonly dialogService = inject(BaseDialogService);
    public readonly translate = inject(TranslateService);

    public center = input<CenterObject | null>(null);
    public personTable = viewChild<CardTableComponent>(CardTableComponent);

    protected form = fb.group({
        userId: fb.control<string | null>(null),
    });

    protected additionalFilter = computed<FilterInput[][]>(() => [
        [{ column: 'type', operator: 'EQ', value: [PersonTypeEnum.REPRESENTATIVE] }],
        [{ column: 'centerId', operator: 'EQ', value: [String(this.center()?.id || 0)] }],
    ]);

    protected columns = [
        {
            name: 'name',
            dataKey: 'user',
        },
        {
            name: 'validFrom',
        },
        {
            name: 'note',
        },
    ];

    protected async contactSelectDialog(): Promise<void> {
        this.dialogService.open(
            OrganizationContactSelectDialogComponent,
            {
                header: this.translate.instant('database.organizationAdministrationPage.statutoryRepresentative.selectStatutoryRepresentativeDialogTitle'),
                styleClass: 'p-dialog-width-30',
                data: {
                    organizationId: routeParams.organizationId(),
                    type: PersonTypeEnum.REPRESENTATIVE,
                    centerId: this.center()?.id || 0,
                },
                focusOnShow: false,
            },
        ).onClose.subscribe((it) => {
            if (it) {
                this.personTable()?.reload();
            }
        });
    }

    protected async upsertContactPersonDialog(person: OrganizationCenterPersonObject | null = null): Promise<void> {
        this.dialogService.open(
            UpsertContactPersonDialogComponent,
            {
                header: this.translate.instant(`database.organizationAdministrationPage.contactPerson.table.${person ? 'detailTitle' : 'createTitle'}`),
                styleClass: 'p-dialog-width-60',
                data: {
                    centerId: this.center()?.id || 0,
                    createRepresentative: true,
                    person,
                },
                focusOnShow: false,
            },
        ).onClose.subscribe((it) => {
            if (it) {
                this.personTable()?.reload();
            }
        });
    }

    public async unlinkPerson(item: any): Promise<void> {
        this.confirm.confirmDelete(
            () => {
                this.store.api.deleteOrganizationCenterPerson({ id: item.item.id }).then(() => {
                    this.personTable()?.reload();
                    contentGlobalSuccess.next('common.deleted');
                });
            },
            item,
        );
    }

    protected readonly isAllowed = isAllowed;
    protected readonly getPersonsFrag = getPersonsFrag;
}
